// Turns a table the page is already showing — headers plus rows of plain
// values — into a real .xlsx and downloads it. The workbook is written in the
// browser, so the file carries exactly the rows, order and columns on screen
// (role, filters, search and sort included) without a second trip to the API.
//
// SheetJS is loaded on demand: it is a large module and only needed at the
// moment someone clicks Download, so it stays out of the main bundle.

import { downloadXlsx } from './download';

/** One cell. Numbers are written as numbers (sortable / summable in Excel);
 *  null, undefined and '' are left as empty cells. Text is always written as
 *  text, so a value starting with '=' is never run as a formula. */
export type TableCell = string | number | null | undefined;

export interface TableSheet {
  /** Sheet tab name. Cleaned to Excel's rules before writing. */
  name: string;
  headers: string[];
  rows: TableCell[][];
  /** Column index → Excel number format (e.g. '0.00') for that column's numeric cells. */
  numberFormats?: Record<number, string>;
}

/** What a mounted table is showing right now, or null when it has no rows to give. */
export type TableSheetGetter = () => TableSheet | null;

type SheetJs = typeof import('xlsx');

const MAX_SHEET_NAME = 31;
const MIN_COL_WIDTH = 8;
const MAX_COL_WIDTH = 60;

const MAX_SLUG = 60;

/** The first `max` characters, never ending on half of an emoji (which corrupts the workbook). */
const cutTo = (text: string, max: number): string => {
  const cut = text.slice(0, max);
  const last = cut.charCodeAt(cut.length - 1);
  return last >= 0xd800 && last <= 0xdbff ? cut.slice(0, -1) : cut;
};

/** Excel refuses a name that starts or ends with an apostrophe; spaces there are noise. */
const trimEdges = (text: string): string => text.replace(/^[\s']+|[\s']+$/g, '');

/**
 * A sheet name Excel accepts, from any text: at most 31 characters, none of
 * \ / ? * [ ] :, no apostrophe at either end, not blank and not 'History'
 * (reserved, in any letter case). Sheet names here are user text — a parameter
 * or sub-metric name, sometimes a whole question — so every rule is applied
 * rather than trusted: one refused name would make that tab impossible to download.
 */
export const sheetNameFor = (name: string): string => {
  const cleaned = trimEdges(name.replace(/[\\/?*[\]:]/g, ' ').replace(/\s+/g, ' '));
  // Edges again after the cut: the 31st character can itself be an apostrophe or a space.
  const cut = trimEdges(cutTo(cleaned, MAX_SHEET_NAME));
  if (!cut) return 'Sheet1';
  return cut.toLowerCase() === 'history' ? `${cut} sheet` : cut;
};

/**
 * A file-name-safe piece from free text: 'Target Achieved %age' → 'target_achieved_age'.
 * Letters and digits of any script are kept (with their combining marks), so a
 * name written in Hindi does not collapse to nothing.
 */
export const fileSlug = (text: string): string =>
  cutTo(text.toLowerCase().replace(/[^\p{L}\p{N}\p{M}]+/gu, '_').replace(/^_+/, ''), MAX_SLUG).replace(/_+$/, '');

const cellValue = (cell: TableCell): string | number | null => {
  if (cell === null || cell === undefined || cell === '') return null;
  if (typeof cell === 'number') return Number.isFinite(cell) ? cell : null;
  return cell;
};

/** Two sheets may not share a name; a repeat gets ' (2)', ' (3)'… within the 31-character limit. */
const uniqueSheetName = (wanted: string, taken: Set<string>): string => {
  let name = wanted;
  for (let n = 2; taken.has(name.toLowerCase()); n++) {
    const suffix = ` (${n})`;
    name = `${trimEdges(cutTo(wanted, MAX_SHEET_NAME - suffix.length)) || 'Sheet'}${suffix}`;
  }
  taken.add(name.toLowerCase());
  return name;
};

/**
 * The workbook for `sheets`: a header row then the data rows, columns sized to
 * their content, a filter on the header row, and the requested number formats.
 * Pure given the SheetJS module, so tests can read the result straight back.
 */
export const buildWorkbook = (XLSX: SheetJs, sheets: TableSheet[]) => {
  const workbook = XLSX.utils.book_new();
  const taken = new Set<string>();
  for (const sheet of sheets) {
    const data = sheet.rows.map((row) => row.map(cellValue));
    const worksheet = XLSX.utils.aoa_to_sheet([sheet.headers, ...data]);

    for (const [col, format] of Object.entries(sheet.numberFormats ?? {})) {
      for (let r = 0; r < data.length; r++) {
        const cell = worksheet[XLSX.utils.encode_cell({ r: r + 1, c: Number(col) })];
        if (cell && cell.t === 'n') cell.z = format;
      }
    }

    worksheet['!cols'] = sheet.headers.map((header, c) => {
      let widest = header.length;
      for (const row of data) {
        const v = row[c];
        if (v !== null && v !== undefined) widest = Math.max(widest, String(v).length);
      }
      return { wch: Math.min(MAX_COL_WIDTH, Math.max(MIN_COL_WIDTH, widest + 2)) };
    });

    if (sheet.headers.length > 0) {
      worksheet['!autofilter'] = {
        ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: data.length, c: sheet.headers.length - 1 } }),
      };
    }

    XLSX.utils.book_append_sheet(workbook, worksheet, uniqueSheetName(sheetNameFor(sheet.name), taken));
  }
  return workbook;
};

/** Writes `sheets` as one .xlsx workbook and downloads it as `filename`. */
export async function downloadTableXlsx(filename: string, sheets: TableSheet | TableSheet[]): Promise<void> {
  const XLSX = await import('xlsx');
  const workbook = buildWorkbook(XLSX, Array.isArray(sheets) ? sheets : [sheets]);
  const bytes = XLSX.write(workbook, { type: 'array', bookType: 'xlsx', compression: true }) as ArrayBuffer;
  downloadXlsx(filename.toLowerCase().endsWith('.xlsx') ? filename : `${filename}.xlsx`, bytes);
}
